<script>
  import { onMount } from 'svelte';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import {
    faRotate,
    faSquarePlus,
    faPencil,
    faTrash,
    faTrashCan,
    faPlus,
    faList,
    faTag,
    faPlusSquare,
  } from '@fortawesome/pro-light-svg-icons';
  import Toolbar from '$lib/Toolbar.svelte';
  import LookupGroupForm from './lookupManagement/LookupGroupForm.svelte';
  import LookupTypeForm from './lookupManagement/LookupTypeForm.svelte';
  import ConfirmationModal from '$lib/ConfirmationModal.svelte';
  import { get } from 'svelte/store';
  import { userRoles, userReadOnly } from '$stores/sessionStore';
  import { studyProperties } from '$stores/configStore';
  import { hasAccessUiAction } from '$utils/permissionUtils.js';
  import FeatureInfo from '$lib/FeatureInfo.svelte';
  import StudyHeader from '$lib/StudyHeader.svelte';

  // Use local API endpoints instead of direct API with key
  const API_BASE_URL = '/api/proxy';

  let lookupGroups = [];
  let selectedGroup = null;
  let lookupTypes = [];
  let resultCount = 0;
  let showGroupForm = false;
  let editGroup = null;
  let showGroupConfirmation = false;
  let groupToDelete = null;
  let showTypeForm = false;
  let editType = null;
  let showTypeConfirmation = false;
  let typeToDelete = null;
  let currentPage = 1;
  let pageSize = 99;
  let sortField = 'groupName';
  let sortOrder = 'asc';

  onMount(async () => {
    fetchLookupGroups();
  });

  async function fetchLookupGroups() {
    try {
      let url = `${API_BASE_URL}/lookupGroups?page=${currentPage}&pageSize=${pageSize}&sortField=${sortField}&sortOrder=${sortOrder}`;

      const res = await fetch(url);

      if (!res.ok) {
        throw new Error('Failed to fetch lookup groups');
      }

      const response = await res.json();
      lookupGroups = Array.isArray(response.data) ? response.data : [];
      resultCount = response.totalCount || 0;
    } catch (err) {
      console.error('Error fetching lookup groups:', err);
      lookupGroups = [];
    }
  }

  async function fetchLookupTypes(lookupGroupGUID) {
    try {
      const res = await fetch(
        `${API_BASE_URL}/lookupTypes/byGroup/${lookupGroupGUID}`
      );

      if (!res.ok) {
        throw new Error('Failed to fetch lookup types');
      }

      const response = await res.json();
      lookupTypes = Array.isArray(response) ? response : [];
    } catch (err) {
      console.error('Error fetching lookup types:', err);
      lookupTypes = [];
    }
  }

  function handleGroupClick(group) {
    selectedGroup = group;
    fetchLookupTypes(group._id);
  }

  function handleRefresh() {
    fetchLookupGroups();
    if (selectedGroup) {
      fetchLookupTypes(selectedGroup._id);
    }
  }

  function handlePageChange(newPage) {
    currentPage = newPage;
    fetchLookupGroups();
  }

  function handleSort(field) {
    if (sortField === field) {
      sortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      sortField = field;
      sortOrder = 'asc';
    }
    currentPage = 1;
    fetchLookupGroups();
  }

  // Lookup Group CRUD operations
  async function handleCreateGroup(event) {
    const newGroup = event.detail;
    try {
      if (!hasAccessUiAction('lookup.addGroups', get(userRoles), get(studyProperties), get(userReadOnly))) {
        console.error('Insufficient permissions to create lookup group');
        return;
      }
      
      const res = await fetch(`${API_BASE_URL}/lookupGroups`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newGroup),
      });

      if (!res.ok) {
        throw new Error('Failed to create lookup group');
      }

      await fetchLookupGroups();
      showGroupForm = false;
    } catch (err) {
      console.error('Error creating lookup group:', err);
    }
  }

  async function handleEditGroup(event) {
    const updatedGroup = event.detail;
    try {
      if (!hasAccessUiAction('lookup.editGroups', get(userRoles), get(studyProperties), get(userReadOnly))) {
        console.error('Insufficient permissions to update lookup group');
        return;
      }
      
      const res = await fetch(`${API_BASE_URL}/lookupGroups/${updatedGroup._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updatedGroup),
      });

      if (!res.ok) {
        throw new Error('Failed to update lookup group');
      }

      await fetchLookupGroups();
      showGroupForm = false;
      editGroup = null;
      
      // Update selected group if it was the one being edited
      if (selectedGroup && selectedGroup._id === updatedGroup._id) {
        selectedGroup = lookupGroups.find(g => g._id === updatedGroup._id);
      }
    } catch (err) {
      console.error('Error updating lookup group:', err);
    }
  }

  async function handleDeleteGroup() {
    if (groupToDelete) {
      try {
        if (!hasAccessUiAction('lookup.deleteGroups', get(userRoles), get(studyProperties), get(userReadOnly))) {
          console.error('Insufficient permissions to delete lookup group');
          return;
        }
        
        const res = await fetch(`${API_BASE_URL}/lookupGroups/${groupToDelete._id}`, {
          method: 'DELETE'
        });

        if (!res.ok) {
          throw new Error('Failed to delete lookup group');
        }

        await fetchLookupGroups();
        showGroupConfirmation = false;
        groupToDelete = null;
        
        // Clear selection if the deleted group was selected
        if (selectedGroup && selectedGroup._id === groupToDelete._id) {
          selectedGroup = null;
          lookupTypes = [];
        }
      } catch (err) {
        console.error('Error deleting lookup group:', err);
      }
    }
  }

  // Lookup Type CRUD operations
  async function handleCreateType(event) {
    const newType = event.detail;
    try {
      if (!hasAccessUiAction('lookup.addTypes', get(userRoles), get(studyProperties), get(userReadOnly))) {
        console.error('Insufficient permissions to create lookup type');
        return;
      }
      
      const res = await fetch(`${API_BASE_URL}/lookupTypes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newType),
      });

      if (!res.ok) {
        throw new Error('Failed to create lookup type');
      }

      if (selectedGroup) {
        await fetchLookupTypes(selectedGroup._id);
      }
      showTypeForm = false;
    } catch (err) {
      console.error('Error creating lookup type:', err);
    }
  }

  async function handleEditType(event) {
    const updatedType = event.detail;
    try {
      if (!hasAccessUiAction('lookup.editTypes', get(userRoles), get(studyProperties), get(userReadOnly))) {
        console.error('Insufficient permissions to update lookup type');
        return;
      }
      
      const res = await fetch(`${API_BASE_URL}/lookupTypes/${updatedType._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updatedType),
      });

      if (!res.ok) {
        throw new Error('Failed to update lookup type');
      }

      if (selectedGroup) {
        await fetchLookupTypes(selectedGroup._id);
      }
      showTypeForm = false;
      editType = null;
    } catch (err) {
      console.error('Error updating lookup type:', err);
    }
  }

  async function handleDeleteType() {
    if (typeToDelete) {
      try {
        if (!hasAccessUiAction('lookup.deleteTypes', get(userRoles), get(studyProperties), get(userReadOnly))) {
          console.error('Insufficient permissions to delete lookup type');
          return;
        }
        
        const res = await fetch(`${API_BASE_URL}/lookupTypes/${typeToDelete._id}`, {
          method: 'DELETE'
        });

        if (!res.ok) {
          throw new Error('Failed to delete lookup type');
        }

        if (selectedGroup) {
          await fetchLookupTypes(selectedGroup._id);
        }
        showTypeConfirmation = false;
        typeToDelete = null;
      } catch (err) {
        console.error('Error deleting lookup type:', err);
      }
    }
  }

  function handleNewGroupClick() {
    editGroup = null;
    showGroupForm = true;
  }

  function handleNewTypeClick() {
    if (!selectedGroup) {
      alert('Please select a lookup group first');
      return;
    }
    editType = null;
    showTypeForm = true;
  }

  function handleEditGroupClick(group) {
    editGroup = group;
    showGroupForm = true;
  }

  function handleEditTypeClick(type) {
    editType = type;
    showTypeForm = true;
  }

  function handleDeleteGroupClick(group) {
    groupToDelete = group;
    showGroupConfirmation = true;
  }

  function handleDeleteTypeClick(type) {
    typeToDelete = type;
    showTypeConfirmation = true;
  }

  // Permission checks
  $: canAddGroups = hasAccessUiAction('lookup.addGroups', $userRoles, $studyProperties, $userReadOnly);
  $: canEditGroups = hasAccessUiAction('lookup.editGroups', $userRoles, $studyProperties, $userReadOnly);
  $: canDeleteGroups = hasAccessUiAction('lookup.deleteGroups', $userRoles, $studyProperties, $userReadOnly);
  $: canAddTypes = hasAccessUiAction('lookup.addTypes', $userRoles, $studyProperties, $userReadOnly);
  $: canEditTypes = hasAccessUiAction('lookup.editTypes', $userRoles, $studyProperties, $userReadOnly);
  $: canDeleteTypes = hasAccessUiAction('lookup.deleteTypes', $userRoles, $studyProperties, $userReadOnly);
</script>

<!-- <svelte:head>
  <title>Lookup Management - SummaPortal</title>
</svelte:head> -->

<StudyHeader />

<FeatureInfo
  title="Lookup Management"
  description="This page allows you to manage lookup groups and their associated lookup types. Lookup groups organize related lookup types (e.g., activity statuses, device types). Each group can contain multiple lookup types with codes, labels, and descriptions. This system provides consistent dropdown options throughout the application."
  marginBottom="mb-0"
/>

<div class="container mx-auto px-1 py-0">
  <div class="grid grid-cols-1 lg:grid-cols-6 gap-3 mt-3">
    <!-- Lookup Groups List -->
    <div class="lg:col-span-3">
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-md">
        <div class="p-2 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center">
            Lookup Groups
          </h3>
          <button
            on:click={handleNewGroupClick}
            disabled={!canAddGroups}
            class:opacity-50={!canAddGroups}
            class="text-sm text-gray-900 dark:text-white mr-2"
            title={!canAddGroups ? 'Insufficient permissions' : 'Add new lookup group'}
          >
          <FontAwesomeIcon icon={faPlusSquare} />
          </button>
        </div>
        <div class="overflow-y-auto" style="max-height: calc(100vh - 150px);">
          {#each lookupGroups as group, index}
            <!-- svelte-ignore a11y-click-events-have-key-events -->
            <!-- svelte-ignore a11y-no-static-element-interactions -->
            <div
              class="px-4 py-2 border-b border-gray-100 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors {selectedGroup?._id === group._id ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700' : ''}"
              on:click={() => handleGroupClick(group)}
            >
              <div class="flex items-center justify-between">
                <div class="flex-1 min-w-0">
                  <div class="flex items-center space-x-3">
                    <div class="flex-1 min-w-0">
                      <div class="flex items-center space-x-2">
                        <h4 class="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{group.groupName}</h4>
                        <span class="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded">{group.code}</span>
                        {#if !group.active}
                          <div class="w-2 h-2 rounded-full bg-red-500" title="Inactive"></div>
                        {/if}
                      </div>
                      {#if group.description}
                        <p class="text-xs text-gray-400 dark:text-gray-500 truncate mt-0.5">{group.description}</p>
                      {/if}
                    </div>
                  </div>
                </div>
                <div class="flex items-center space-x-1 ml-2">
                  <button
                    on:click|stopPropagation={() => handleEditGroupClick(group)}
                    disabled={!canEditGroups}
                    class:opacity-50={!canEditGroups}
                    class="pagination mx-1 p-0"
                    title={!canEditGroups ? 'Insufficient permissions' : 'Edit group'}
                  >
                    <FontAwesomeIcon icon={faPencil} class="text-sky-800 dark:text-white/70" />
                  </button>
                  <button
                    on:click|stopPropagation={() => handleDeleteGroupClick(group)}
                    disabled={!canDeleteGroups}
                    class:opacity-50={!canDeleteGroups}
                    class="pagination mx-1 p-0"
                    title={!canDeleteGroups ? 'Insufficient permissions' : 'Delete group'}
                  >
                    <FontAwesomeIcon icon={faTrashCan} class="text-sky-800 dark:text-white/70" />
                  </button>
                </div>
              </div>
            </div>
          {/each}
          {#if lookupGroups.length === 0}
            <div class="p-4 text-center text-gray-500 dark:text-gray-400">
              No lookup groups found
            </div>
          {/if}
        </div>
      </div>
    </div>

    <!-- Lookup Types List -->
    <div class="lg:col-span-3">
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-md">
        <div class="p-2 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center">
            Lookup Values
            {#if selectedGroup}
              <span class="text-sm font-normal text-gray-500 dark:text-gray-400 ml-2">
                - {selectedGroup.groupName}
              </span>
            {/if}
          </h3>
          {#if selectedGroup}
            <button
              on:click={handleNewTypeClick}
              disabled={!canAddTypes}
              class:opacity-50={!canAddTypes}
              class="text-sm text-gray-900 dark:text-white mr-2"
              title={!canAddTypes ? 'Insufficient permissions' : 'Add new lookup type'}
            >
              <FontAwesomeIcon icon={faPlusSquare} />
            </button>
          {/if}
        </div>
        <div class="overflow-y-auto" style="max-height: calc(100vh - 150px);">
          {#if selectedGroup}
            {#each lookupTypes as type, index}
              <div class="px-4 py-2 border-b border-gray-100 dark:border-gray-700">
                <div class="flex items-center justify-between">
                  <div class="flex-1 min-w-0">
                    <div class="flex items-center space-x-3">
                      <div class="flex-1 min-w-0">
                        <div class="flex items-center space-x-2">
                          <h4 class="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{type.label}</h4>
                          <span class="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded">{type.code}</span>
                          {#if !type.active}
                            <div class="w-2 h-2 rounded-full bg-red-500" title="Inactive"></div>
                          {/if}
                          {#if type.default}
                            <span class="text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 px-2 py-0.5 rounded">Default</span>
                          {/if}
                        </div>
                        {#if type.description}
                          <p class="text-xs text-gray-400 dark:text-gray-500 truncate mt-0.5">{type.description}</p>
                        {/if}
                      </div>
                    </div>
                  </div>
                  <div class="flex items-center space-x-1 ml-2">
                    <button
                      on:click={() => handleEditTypeClick(type)}
                      disabled={!canEditTypes}
                      class:opacity-50={!canEditTypes}
                      class="pagination mx-1 p-0"
                      title={!canEditTypes ? 'Insufficient permissions' : 'Edit type'}
                    >
                      <FontAwesomeIcon icon={faPencil} class="text-sky-800 dark:text-white/70" />
                    </button>
                    <button
                      on:click={() => handleDeleteTypeClick(type)}
                      disabled={!canDeleteTypes}
                      class:opacity-50={!canDeleteTypes}
                      class="pagination mx-1 p-0"
                      title={!canDeleteTypes ? 'Insufficient permissions' : 'Delete type'}
                    >
                      <FontAwesomeIcon icon={faTrashCan} class="text-sky-800 dark:text-white/70" />
                    </button>
                  </div>
                </div>
              </div>
            {/each}
            {#if lookupTypes.length === 0}
              <div class="p-4 text-center text-gray-500 dark:text-gray-400">
                No lookup types found for this group
              </div>
            {/if}
          {:else}
            <div class="p-4 text-center text-gray-500 dark:text-gray-400">
              Select a lookup group to view its types
            </div>
          {/if}
        </div>
      </div>
    </div>
  </div>
</div>

<!-- Modals -->
{#if showGroupForm}
  <LookupGroupForm
    {editGroup}
    on:create={handleCreateGroup}
    on:update={handleEditGroup}
    on:cancel={() => {
      showGroupForm = false;
      editGroup = null;
    }}
  />
{/if}

{#if showTypeForm}
  <LookupTypeForm
    {editType}
    {selectedGroup}
    on:create={handleCreateType}
    on:update={handleEditType}
    on:cancel={() => {
      showTypeForm = false;
      editType = null;
    }}
  />
{/if}

{#if showGroupConfirmation}
  <ConfirmationModal
    showConfirmation={true}
    message={`Are you sure you want to delete the lookup group "${groupToDelete?.groupName}"? This action cannot be undone.`}
    confirmPhrase="delete"
    on:confirm={handleDeleteGroup}
    on:close={() => {
      showGroupConfirmation = false;
      groupToDelete = null;
    }}
  />
{/if}

{#if showTypeConfirmation}
  <ConfirmationModal
    showConfirmation={true}
    message={`Are you sure you want to delete the lookup type "${typeToDelete?.label}"? This action cannot be undone.`}
    confirmPhrase="delete"
    on:confirm={handleDeleteType}
    on:close={() => {
      showTypeConfirmation = false;
      typeToDelete = null;
    }}
  />
{/if} 